import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { debounce } from 'lodash';
import { callWithRetry } from '../../utils/fetchWrapper';
import {
  CustomizeSurveyBanner,
  CustomizeSurveyNavigation,
  CustomizeSurveyPrevQuestion,
  CustomizeSurveyRegionsOfInterest,
  CustomizeRandoxPostcodeClinicSearch,
  CustomizeSurveySummary,
  CustomizeSurveyYouTube,
  CustomizeSurveyImage,
  CustomizeSurveyInuviFields
} from './customWidgets';
import { CustomizeSurveyAvailability, CustomizePatientAvailability } from './customFunctions';
import Survey from '../Survey';
import SurveySubmissionModal from '../Modals/SurveySubmissionModal';
import { Auth } from 'aws-amplify';
import { useRuntimeConfig } from 'vl-common/src/hooks/Runtime';
import {
  ChoicesRestful,
  ITheme,
  Model,
  QuestionFileModel,
  QuestionSignaturePadModel,
  Serializer,
  surveyLocalization,
  SurveyModel,
  UploadFilesEvent
} from 'survey-core';
import { PlainLightPanelless } from 'survey-core/themes';
import { confirmationModal, errorModal, infoModal } from '../Modals';
import styled from '@emotion/styled';
import SurveyTableView, { FileWithContent, FileWithoutContent } from '../SurveyTableView';
import breakpoints from 'vl-common/src/lib/breakpoints';
import { Dispatch } from 'react-redux';
import type { User, Survey as TSurvey } from 'vl-common/src/schemas/shared';

import CustomizeClass2MedDevSurveyAction, {
  disableActionButton,
  HBSQuestion
} from 'vl-md/src/CustomSurvey/customWidgets/CustomizeClass2MedDevSurveyAction';
import CustomizeNonClass2MedDevSurveyAction from './customWidgets/CustomizeNonClass2MedDevSurveyAction';
import { Class2MedDevModel } from 'vl-md/src/CustomSurvey/customWidgets/Class2MedDevModel';
import ServiceApi from '../../services/service.api';
import addHours from 'date-fns/addHours';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import CustomizeSurveyRandoxAddressFields, {
  CUSTOM_RANDOX_HOME_ADDRESS_QUESTION_TYPE
} from './customWidgets/CustomizeSurveyRandoxAddressFields';
import { LabTest } from '../../schemas/shared/lab';
import useCreateRandoxOrderMutation, {
  combineOrderAddressParts,
  combineOrderLabTests,
  CreateRandoxOrderTypes,
  hasOrderUrineTest
} from '../../hooks/useCreateRandoxOrderMutations';
import { InuviOrderParams, RandoxHomeTestKitParams, RandoxInClinicParams } from '../../api/api.randox';
import { CUSTOM_RANDOX_POSTCODE_CLINIC_SEARCH_QUESTION_TYPE } from './customWidgets/CustomizeRandoxPostcodeClinicSearch';
import { CUSTOM_INUVI_ORDER_QUESTION_TYPE } from './customWidgets/CustomizeInuviSurveyFields';
import caseInsensitiveObjectSearch from '../../lib/caseInsensitiveObjectSearch';

Serializer.addProperty('question', {
  name: 'Dermoscopy Outcome:boolean',
  category: 'general',
  default: false,
  visibleIndex: 1
});

const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

/**  Used as a placeholder when previewing non-image files - implemented in VLSurveyStyleOverrides.
 * Also used when the user doesn't have read permissions when uploading a file. */
const previewUnavailable =
  'data:image/png;base64,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';

export type SaveTriageCallback = (
  outcome: string,
  user_guid: string,
  caseId: number,
  spec_area_code: string,
  clin_subtype: string,
  discharge_code?: string
) => Promise<unknown>;

export type CreateTaskCallback = (
  question: HBSQuestion,
  user_guid: string,
  caseId: number,
  backendTaskRef: string | null,
  due_date_value: string,
  appointmentId: number,
  surveyjsId: number,
  queue: string
) => Promise<unknown>;

export type BookAppointmentCallback = (
  caseId: number,
  specCatCode: string,
  areaCode: string,
  clinicianTypeCode: string,
  appointmentId: number
) => Promise<void> | void;

export type BookFaceToFaceCallback = (caseId: number, clinicianTypeCode: string, appointmentId: number) => void;

export type UpdateUrgencyCallback = (user_guid: string, caseId: number, urgencyType: string) => Promise<unknown>;

export type CreateCaseCallback = (params: {
  pat_guid: string;
  specialty: string;
  subSpecialty: string;
  notes: string;
  patient_sex?: string;
}) => Promise<boolean>;

export type SetCasePathwayCallback = UseMutateAsyncFunction<any, unknown, 'F2F' | 'CR', unknown>;

type ChangeRandoxOrderStatusCallback = UseMutateAsyncFunction<
  any,
  unknown,
  {
    user_guid: string;
    appt_id: number;
    order_route_code: string;
    status: string;
  }
>;

type SaveSurveyCallback = (data: {
  user_guid: string;
  surveyjs_id: number;
  complete: boolean;
  response_json: string;
}) => Promise<void>;

type SaveSurveyToS3Callback = (
  originalAnswers: unknown,
  definition: unknown,
  surveyObject: {
    survey_id: number;
    patient_user_guid: string;
    cat_code: string;
  },
  file: string,
  dispatch: Dispatch,
  case_id?: number | null,
  surveyjsLicenseKey?: string,
  setCompletingPercent?: (val: number) => void,
  setProgressMessage?: (msg: string) => void,
  setModalVisible?: (val: boolean) => void
) => Promise<void>;

type SubmitCaseTriageCallback = (
  outcome: string,
  user_guid: string,
  caseId: number,
  spec_area_code: string,
  clin_subtype: string,
  discharge_code?: string
) => Promise<unknown>;

type UploadEmbeddedImagesToS3Callback = (
  survey: SurveyModel,
  owner_guid: string,
  surveyjsId: number,
  dispatch: Dispatch,
  appt_id: number
) => Promise<void>;

type UploadImagesWithCropToS3Callback = (
  images: unknown[],
  owner_guid: string,
  surveyjsId: number,
  dispatch: Dispatch,
  userHasReadPermissions: boolean
) => Promise<unknown>;

type GetSurveyImageFromS3Callback = (
  name: string,
  patientUserGuid: string,
  userHasReadPermissions: boolean,
  surveyjsId: number | string,
  dispatch: Dispatch,
  skipReplacement?: boolean
) => Promise<string>;

type UploadFileToS3Callback = (
  file: File,
  dispatch: Dispatch,
  user_guid: string,
  userHasReadPermissions: boolean,
  surveyjsId: number
) => Promise<{ file: File; url: string | null }>;

interface SurveyDefaultFields {
  API_HOST: string;
  APPUSER: string;
  CanSelfRefer: boolean;
  MembershipNo: string;
  Pathway: string | null;
  PatientDefaultAddress: string;
  PatientFirstName: string;
  PatientLastName: string;
  RandoxLabTests: string;
  RestrictedUser: boolean;
  Urgency: string;
  currentPageNo: number;
}

export type SurveyResponse = SurveyDefaultFields & {
  [key: string]: string | number | boolean | null | undefined;
};

const ACTION_OUTCOMES = {
  'Book face-to-face consultant': {
    outcomeType: 'F2F-CONSULT',
    backendTaskRef: null
  },
  'Go to case review': {
    outcomeType: 'CASEREV',
    backendTaskRef: null
  }
};

const TASK_OUTCOMES = {
  'Book Appointment': {
    outcomeType: 'BKAPPT',
    backendTaskRef: 'BOOK-APPOINTMENT'
  },
  'Book face-to-face consultant': {
    outcomeType: 'F2F-CONSULT',
    backendTaskRef: null
  },
  'Call patient': {
    outcomeType: 'TASK',
    backendTaskRef: 'CALL-PATIENT'
  },
  'Go to case review': {
    outcomeType: 'CASEREV',
    backendTaskRef: null
  },
  'Red flag': {
    outcomeType: 'RED-FLAG',
    backendTaskRef: 'RED-FLAG'
  },
  'F2F Physio': {
    outcomeType: 'F2F-PHYSIO',
    backendTaskRef: 'F2F-PHYSIO'
  },
  'F2F Consult': {
    outcomeType: 'F2F-CONSULT',
    backendTaskRef: 'F2F-CONSULT'
  },
  'Patient requested follow up': {
    outcomeType: 'PAT-REQ-FOLLOW',
    backendTaskRef: 'PAT-REQ-FOLLOW'
  },
  'Dermoscopy Outcome': {
    outcomeType: 'DERMOSCOPY',
    backendTaskRef: 'DERMOSCOPY'
  }
};

const UNRELATED_TASK_TYPE = {
  outcomeType: 'TASK',
  backendTaskRef: 'OTHER'
};

function getOutcome(
  typeOfTask: string,
  actionType: string
): {
  outcomeType: string;
  backendTaskRef: string | null;
} {
  const maybeActionOutcome =
    actionType in ACTION_OUTCOMES ? ACTION_OUTCOMES[actionType as keyof typeof ACTION_OUTCOMES] : null;
  const maybeTaskOutcome = typeOfTask in TASK_OUTCOMES ? TASK_OUTCOMES[typeOfTask as keyof typeof TASK_OUTCOMES] : null;

  return maybeActionOutcome || maybeTaskOutcome || UNRELATED_TASK_TYPE;
}

const getSurveyValueByQuestionType = (survey: Class2MedDevModel, type: string) => {
  return survey.getAllQuestions().find((q) => q.getType() === type)?.value;
};

interface CustomSurveyProps {
  bookAppointment: BookAppointmentCallback | null;
  bookFaceToFace: BookFaceToFaceCallback | null;
  caseReview?: boolean;
  createTask: CreateTaskCallback | null;
  dispatch: Dispatch;
  getExpandedAnswers?: (<T>(answers: T, surveyjsId: number) => Promise<T>) | null;
  getUsers: () => Promise<(User & { source_url?: string })[]> | ((User & { source_url?: string }) | null)[];
  modalVisible: boolean;
  onData?: ((survey: SurveyModel, surveyjsId: number) => void) | null;
  pageType: 'clinician' | 'patients';
  rawSurveyDefinition: Record<string, any>;
  readonly?: boolean;
  removeEmbeddedImages?: (<T>(data: T) => Promise<T>) | null;
  routeBackToDashboard?: () => void;
  saveSurvey?: SaveSurveyCallback | null;
  saveSurveyToS3?: SaveSurveyToS3Callback | null;
  setModalVisible?: React.Dispatch<React.SetStateAction<boolean>> | null;
  submitCaseTriage?: SubmitCaseTriageCallback | null;
  surveyRes: TSurvey;
  uploadEmbeddedImagesToS3?: UploadEmbeddedImagesToS3Callback | null;
  uploadImagesWithCropToS3?: UploadImagesWithCropToS3Callback | null;
  getSurveyImageFromS3?: GetSurveyImageFromS3Callback;
  uploadFileToS3?: UploadFileToS3Callback | null;
  user: User;
  userIsRestricted: boolean;
  // userNeedsRecentCardDetails?: boolean;
  updateUrgency: UpdateUrgencyCallback | null;
  getCaseUrgency?: () => Promise<string> | null;
  setCasePathway?: SetCasePathwayCallback;
  /** must be set for the survey to handle completion. If not set, survey is never saved and only used as a "smart" form */
  shouldComplete?: boolean;
  createCase?: CreateCaseCallback;
  onDischarge?: () => void;
  isLoadingSurveyData: boolean;
  isCompleted?: boolean;
  hasReadPermissions?: boolean;
  inTableView?: boolean;
  confirmationModalTitle?: string;
  surveyLabTestOrder?: LabTest | null;
  createRandoxOrder?: ReturnType<typeof useCreateRandoxOrderMutation>;
  surveyAnswersOnComplete?: (surveyAnswers: SurveyResponse) => void;
  changeRandoxOrderStatus?: ChangeRandoxOrderStatusCallback;
  showCookieSettings?: () => void;
}

const surveyTheme: ITheme = {
  ...PlainLightPanelless,
  cssVariables: {
    ...PlainLightPanelless.cssVariables,
    '--sjs-font-family': 'var(--fontPrimary)',
    '--sjs-primary-backcolor': 'var(--productPrimary)',
    '--sjs-primary-backcolor-light': 'var(--sjs-general-backcolor-dim-dark)',
    '--sjs-primary-backcolor-dark': 'var(--productPrimaryDark)',
    '--sjs-general-forecolor-light': 'var(--sjs-general-forecolor)',
    '--sjs-general-dim-forecolor-light': 'var(--sjs-general-dim-forecolor)'
  }
};

const VLSurveyStyleOverrides = styled.div`
  .sd-boolean--disabled .sd-boolean__thumb,
  .sd-boolean--disabled .sd-boolean__label,
  .sd-item--disabled .sd-item__control-label,
  .sd-root--readonly .sd-input--disabled,
  .sd-root--readonly .sd-input--disabled::placeholder,
  .sd-question--disabled .sd-rating__item-text,
  .sd-item--disabled.sd-item--disabled .sd-item__decorator,
  .sd-item__decorator {
    opacity: unset;
  }

  .sd-question--answered {
    white-space: normal;
  }

  // Solid solution to show custom preview image for non-image files and hide default icon
  .sd-file__default-image {
    background-image: url(${previewUnavailable});
    background-size: cover;
    background-position: center;
    width: 80%;
    height: 80%;
    use {
      display: none;
    }
  }

  .sd-imagepicker__item--checked .sd-imagepicker__image-container,
  .sd-imagepicker__item:focus-within .sd-imagepicker__image {
    outline: 3px solid var(--productPrimary);
    outline-offset: 1px;
    border-radius: 2px;
    opacity: 1 !important;
  }

  .sd-imagepicker__item:hover .sd-imagepicker__image-container {
    outline: 3px solid var(--productPrimaryHover);
    outline-offset: 1px;
    border-radius: 2px;
    cursor: pointer;
  }

  @media (min-width: ${breakpoints.md}px) {
    .sd-file__list {
      max-height: unset;
    }

    .sd-file__image-wrapper {
      height: calc(48 * (var(--sjs-base-unit, var(--base-unit, 80px))));

      img {
        width: 100%;
        height: 100%;
      }
    }
  }

  .sd-file--single-image {
    height: calc(48 * var(--sjs-base-unit, var(--base-unit, 8px)));

    .sd-file__list {
      max-height: unset;
    }
    .sd-file__image-wrapper {
      min-height: 80%;
      height: unset;
    }
    .sd-file__sign {
      min-height: unset;
      position: relative;
      a {
        color: black;
      }
    }
  }

  .sd-file__remove-file-button {
    opacity: 100%;
    left: 100%;
    top: 10%;
    border: 1px solid grey;
  }

  .sd-item--error .sd-checkbox__control ~ .sd-item__decorator {
    background: unset;
  }
  .sd-boolean__thumb {
    color: white;
    background-color: var(--productPrimary);
  }

  body:not([data-contrast='high']) & {
    .sd-boolean__thumb {
      color: var(--primary-btn-font-color);
    }

    .sd-btn--action:not(:hover) {
      color: var(--primary-btn-font-color);
    }

    .sd-btn--action:hover {
      background: var(--ant-primary-color-hover);
      color: var(--primary-btn-font-color);
    }
  }
`;

const updateFileQuestions = (json: string) => {
  const parsedJson = JSON.parse(json);
  return JSON.stringify({
    ...parsedJson,
    pages:
      parsedJson?.pages?.map((page: any) => {
        page?.elements?.forEach((element: any) => {
          if (element?.type === 'file' && !element?.imageHeight) {
            // if imageHeight is not specified in the Survey Settings, set it to 250 pixels,
            // so the image will not be stretched to the height of the screen
            element.imageHeight = '250px';
          }
        });
        return page;
      }) || []
  });
};

const CLINICIAN_TYPES = {
  Physio: 'PHYSIO',
  'Advanced Physio': 'APHYSIO',
  Consultant: 'CONSULT'
} as const;

CustomizeSurveyBanner();
CustomizeSurveyYouTube();
CustomizeSurveyImage();
CustomizeSurveyNavigation();
CustomizeSurveyAvailability();
CustomizeSurveySummary();
CustomizeSurveyPrevQuestion();
CustomizeSurveyRegionsOfInterest();
CustomizeRandoxPostcodeClinicSearch();
CustomizeSurveyRandoxAddressFields();
CustomizeClass2MedDevSurveyAction();
CustomizeNonClass2MedDevSurveyAction();
CustomizeSurveyInuviFields();

// Used to work out if the survey has a dermoscopy outcome by checking if any
// questions have the flag, what answer has been provided and
// whether the survey outcome should change
function isDermoscopyOutcome(surv_data: any, surveyDefn: any) {
  const rejectedAnswers = [false, null, undefined, ''];
  const surveyTitle = surveyDefn.pages[0].elements[0].html || '';

  if (surveyTitle?.includes('Dermatology')) {
    const results = surveyDefn.pages.flatMap((item: any) =>
      item.elements.filter((item: any) => item['Dermoscopy Outcome'])
    );

    if (!results.length) return false;

    const questionNames = results.map((item: any) => item.name);
    const questionAnswers = questionNames.map((item: string) => surv_data[item]);

    const finalOutcome = rejectedAnswers.some((value: any) => questionAnswers.includes(value));
    return !finalOutcome;
  }

  return false;
}

const CustomSurvey: React.FC<CustomSurveyProps> = ({
  bookAppointment,
  bookFaceToFace,
  caseReview,
  createTask,
  dispatch,
  getExpandedAnswers,
  getUsers,
  modalVisible,
  onData,
  pageType,
  rawSurveyDefinition,
  removeEmbeddedImages = (input) => Promise.resolve(input),
  readonly,
  routeBackToDashboard,
  saveSurvey,
  saveSurveyToS3,
  setModalVisible,
  submitCaseTriage,
  surveyRes,
  uploadEmbeddedImagesToS3,
  uploadImagesWithCropToS3,
  getSurveyImageFromS3,
  uploadFileToS3,
  user,
  userIsRestricted,
  updateUrgency,
  setCasePathway,
  getCaseUrgency,
  createCase,
  // userNeedsRecentCardDetails,
  shouldComplete = true,
  isLoadingSurveyData,
  isCompleted,
  onDischarge,
  hasReadPermissions = false,
  inTableView = false,
  confirmationModalTitle = 'Are you sure you want to complete?',
  surveyLabTestOrder,
  createRandoxOrder,
  surveyAnswersOnComplete,
  changeRandoxOrderStatus,
  showCookieSettings = () => {}
}) => {
  const { SURVEYJS_LICENSE_KEY } = useRuntimeConfig();
  const { appt_id, case_id, cat_code: specCatCode, surveyjs_id: surveyjsId } = surveyRes;

  // Not good, but they generally _are_ set and need to be
  const appointmentId = appt_id!;
  const caseId = case_id!;

  const dischargeCode = useRef<string | undefined>(undefined);
  const outcome = useRef<string>('');
  const savedClinicianTypeCode = useRef<string>('');
  const savedAreaCode = useRef<string>('');
  // Once completion starts, blocks the draft auto-save — the case may already be closed.
  const isCompleting = useRef(false);
  const { user_guid } = user;
  const userHasReadPermissions = hasReadPermissions;

  const [surveyModel, setSurveyModel] = useState<Class2MedDevModel>();
  const [isConfiguringSurveyModel, setIsConfiguringSurveyModel] = useState(true);
  const [updatedSurveyRes, setUpdatedSurveyRes] = useState({ ...surveyRes });
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAsComplete, setIsSavingAsComplete] = useState(false);
  const [completingPercent, setCompletingPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const { API_URL } = useRuntimeConfig();

  // Hack to avoid multiple runs of saveTriage without refactoring the massive useEffect
  const [triageSaved, setTriageSaved] = useState(false);

  const saveTriage: SaveTriageCallback = useCallback(
    async (
      outcome: string,
      user_guid: string,
      caseId: number,
      spec_area_code: string,
      clin_subtype: string,
      discharge_code?: string
    ) => {
      if (triageSaved) return;

      try {
        await submitCaseTriage?.(outcome, user_guid, caseId, spec_area_code, clin_subtype, discharge_code);
        setTriageSaved(true);
      } catch (error) {
        console.error('Failed to save triage', error);
      }
    },
    [submitCaseTriage, triageSaved]
  );

  useEffect(() => {
    let credentialsPoll: NodeJS.Timeout;
    isCompleting.current = false;
    if (rawSurveyDefinition && Object.keys(rawSurveyDefinition).length > 0) {
      const { clin_editable, always_edit, cat_code } = rawSurveyDefinition;
      const surveyJson = updateFileQuestions(rawSurveyDefinition.survey_json || '{"pages":[]}');
      const response = updatedSurveyRes?.response;
      const answers = response ? JSON.parse(response) : null;

      const configureSurveyModel = async () => {
        const incomplete = isCompleted !== undefined ? !isCompleted : !surveyRes.completed_flag;
        let bearerToken = (await Auth.currentSession()).getIdToken().getJwtToken();
        credentialsPoll = setInterval(async () => {
          try {
            const tokens = await Auth.currentSession();
            bearerToken = tokens.getIdToken().getJwtToken();
          } catch {
            clearInterval(credentialsPoll);
          }
        }, 30 * 1000);

        ChoicesRestful.clearCache();
        ChoicesRestful.EncodeParameters = false;
        // Add authorisation hooks so we can access the VL backend
        ChoicesRestful.onBeforeSendRequest = (sender, options) => {
          // @ts-ignore - Survey request type compatibility
          options.request.setRequestHeader('Authorization', `Bearer ${bearerToken}`);
        };

        class HBSModel extends Class2MedDevModel {
          constructor() {
            super(user_guid, caseId, appointmentId, surveyjsId, ServiceApi.refreshToken, API_URL);
          }

          async handleAction(question: HBSQuestion, actionButton: HTMLButtonElement): Promise<void> {
            const {
              taskType,
              actionType,
              urgencyType,
              caseDischargeReason,
              subSpecialty,
              clinicianType,
              randoxOrderStatus
            } = question.toJSON({
              storeDefaults: true
            });
            const { outcomeType, backendTaskRef } = getOutcome(taskType, actionType);
            const regex = /{([\S]+)}/; //NOSONAR
            const found = subSpecialty?.match(regex);
            let areaCode = null;

            outcome.current = outcomeType;
            if (found) {
              areaCode = question.survey.getQuestionByValueName(found[1])?.value;
            } else if (subSpecialty) {
              areaCode = subSpecialty;
            } else {
              areaCode = question.survey.getQuestionByValueName('SubSpecialty')?.value;
            }
            savedAreaCode.current = areaCode;

            const getClinicianTypeCode = (clinicianType: string) =>
              clinicianType in CLINICIAN_TYPES ? CLINICIAN_TYPES[clinicianType as keyof typeof CLINICIAN_TYPES] : '';

            const clinicianTypeCode = getClinicianTypeCode(clinicianType);
            savedClinicianTypeCode.current = clinicianTypeCode;
            let shouldDisableActionButton: boolean | undefined = true;

            const answers = this.data; //NOSONAR

            const triageExcludedActions = [
              'Set discharge reason',
              'Create Case',
              'Create task',
              'Set pathway status to case review',
              'Set pathway status to face-to-face',
              'Create Home Test Kit Order',
              'Create In Person Order',
              'Set Lab Test Order Status'
            ];

            if (!triageExcludedActions.includes(actionType)) {
              answers.currentPageNo = survey.currentPageNo;
              await saveSurvey?.({
                user_guid,
                surveyjs_id: surveyjsId,
                complete: false,
                response_json: JSON.stringify(await removeEmbeddedImages?.(answers), (key, value) =>
                  key === 'dataURL' || key === 'highRes' ? undefined : value
                )
              });
              await saveTriage(outcomeType, user_guid, caseId, areaCode, clinicianTypeCode);
            }

            const handleCreateRandoxOrder = async (
              type: keyof typeof CreateRandoxOrderTypes,
              data: RandoxInClinicParams | RandoxHomeTestKitParams | InuviOrderParams
            ) => {
              if (!createRandoxOrder) return;

              actionButton.innerText = 'Creating order...';
              actionButton.style.cursor = 'default';
              actionButton.disabled = true;

              try {
                await createRandoxOrder(type, data);
                survey.skipConfirmation = true;
                survey.doComplete();
              } catch (e) {
                errorModal({
                  title: 'Error',
                  // @ts-expect-error
                  content: e?.response?.data?.responseError || 'Error while creating order'
                });
              } finally {
                actionButton.innerText = 'Create order';
                actionButton.style.cursor = 'pointer';
                actionButton.disabled = false;
              }
            };

            switch (actionType) {
              case 'Book appointment':
                // confirmation modal does work as routing immediately happens on book appointment click
                survey.skipConfirmation = true;
                survey.doComplete();
                bookAppointment?.(caseId, specCatCode, areaCode, clinicianTypeCode, appointmentId);
                break;

              case 'Book face-to-face consultant':
                bookFaceToFace?.(caseId, clinicianTypeCode, appointmentId);
                break;

              case 'Set urgency':
                updateUrgency?.(user.user_guid, caseId, urgencyType);
                break;

              case 'Set discharge reason':
                outcome.current = 'DISCHARGE';
                dischargeCode.current = caseDischargeReason;

                onDischarge ? onDischarge() : survey.completeLastPage();
                break;

              case 'Create Case':
                actionButton.innerText = 'Creating case...';
                actionButton.style.cursor = 'default';
                actionButton.disabled = true;

                shouldDisableActionButton = await createCase?.({
                  pat_guid: user.user_guid,
                  specialty: caseInsensitiveObjectSearch(answers, 'specialty'),
                  subSpecialty: caseInsensitiveObjectSearch(answers, 'subspecialty'),
                  notes: caseInsensitiveObjectSearch(answers, 'notes'),
                  patient_sex: caseInsensitiveObjectSearch(answers, 'sexatbirth')
                });

                actionButton.innerText = 'Create Case';
                actionButton.style.cursor = 'pointer';
                actionButton.disabled = false;
                break;

              // case 'Setup Card':
              //   window.sessionStorage.setItem('cardSetup', 'true');
              //   window.sessionStorage.setItem('cardSetupAction', 'Create new case');
              //   window.sessionStorage.setItem(
              //     'cardSetupRedirectLink',
              //     Buffer.from('/patients?newcase=1').toString('base64')
              //   );
              //   window.location.replace('/patients/profile');
              //   break;

              case 'Set pathway status to case review':
                shouldDisableActionButton = false;
                setCasePathway?.('CR', {
                  onSuccess: () => {
                    question.survey.nextPage();
                  }
                });
                break;

              case 'Set pathway status to face-to-face':
                shouldDisableActionButton = false;

                setCasePathway?.('F2F', {
                  onSuccess: () => survey.completeLastPage()
                });
                break;

              case 'Create Home Test Kit Order': {
                if (!surveyLabTestOrder) return;
                shouldDisableActionButton = false;

                const providedAlternativeAdress = getSurveyValueByQuestionType(
                  survey,
                  CUSTOM_RANDOX_HOME_ADDRESS_QUESTION_TYPE
                );
                const useDefaultAddress =
                  !providedAlternativeAdress?.postcode && !providedAlternativeAdress?.addressLine1;
                const defaultAddress = surveyLabTestOrder.patient_address;

                const orderData: RandoxHomeTestKitParams = {
                  appt_id: surveyLabTestOrder.appt_id,
                  order_route_code: surveyLabTestOrder.order_route_code as string,
                  postcode: useDefaultAddress ? defaultAddress.postcode : providedAlternativeAdress?.postcode,
                  addline1: useDefaultAddress ? defaultAddress.addline1 : providedAlternativeAdress?.addressLine1,
                  addline2: useDefaultAddress ? defaultAddress.addline2 : providedAlternativeAdress?.addressLine2,
                  city: useDefaultAddress ? defaultAddress.addline4 : providedAlternativeAdress?.town,
                  country: useDefaultAddress ? defaultAddress.country : providedAlternativeAdress?.country
                };

                await handleCreateRandoxOrder(CreateRandoxOrderTypes.HOME_TEST_KIT, orderData);
                break;
              }

              case 'Create In Person Order': {
                if (!surveyLabTestOrder) return;
                shouldDisableActionButton = false;

                const clinicSlotData = getSurveyValueByQuestionType(
                  survey,
                  CUSTOM_RANDOX_POSTCODE_CLINIC_SEARCH_QUESTION_TYPE
                );

                if (clinicSlotData?.slotId && !clinicSlotData?.isNotInRandoxCatchment) {
                  const inClinicOrderData: RandoxInClinicParams = {
                    appt_id: surveyLabTestOrder.appt_id,
                    location_id: clinicSlotData.id,
                    slot_id: clinicSlotData.slotId,
                    slot_date: clinicSlotData.date,
                    slot_time: clinicSlotData.time
                  };

                  await handleCreateRandoxOrder(CreateRandoxOrderTypes.RANDOX_IN_CLINIC, inClinicOrderData);
                  break;
                }

                const inuviSurveyData = getSurveyValueByQuestionType(survey, CUSTOM_INUVI_ORDER_QUESTION_TYPE);

                const inuviOrderData: InuviOrderParams = {
                  appt_id: surveyLabTestOrder.appt_id,
                  case_id: surveyLabTestOrder.case_id,
                  pat_guid: surveyLabTestOrder.user_guid,
                  policyNumber: caseInsensitiveObjectSearch(answers, 'MembershipNo'),
                  order_route_code: surveyLabTestOrder.order_route_code || '',
                  lab_tests: surveyLabTestOrder.lab_tests.map(({ lab_test_id, service_type_code, randox_code }) => ({
                    id: lab_test_id,
                    service_code: service_type_code,
                    randox_code
                  })),
                  addline1: inuviSurveyData.addline1,
                  addline2: inuviSurveyData.addline2,
                  addline3: inuviSurveyData.addline3,
                  city: inuviSurveyData.city,
                  postcode: inuviSurveyData.postcode
                };

                await handleCreateRandoxOrder(CreateRandoxOrderTypes.INUVI_IN_PERSON, inuviOrderData);
                break;
              }
              case 'Set Lab Test Order Status': {
                if (!surveyLabTestOrder) return;
                shouldDisableActionButton = false;

                actionButton.style.cursor = 'default';
                actionButton.disabled = true;

                try {
                  await changeRandoxOrderStatus?.({
                    user_guid: surveyLabTestOrder.user_guid,
                    appt_id: surveyLabTestOrder.appt_id,
                    order_route_code: surveyLabTestOrder.order_route_code as string,
                    status: randoxOrderStatus
                  });
                  survey.skipConfirmation = true;
                  survey.doComplete();
                } catch (e) {
                  console.log('Error while changing randox order status: ', e);
                } finally {
                  actionButton.style.cursor = 'pointer';
                  actionButton.disabled = false;
                }
                break;
              }

              // Includes 'Create task'!
              default:
                {
                  const { taskQueue, taskDeadlineInHours } = question.toJSON({ storeDefaults: true });
                  const due_date_value = addHours(new Date(), taskDeadlineInHours);

                  shouldDisableActionButton = false;
                  const completionSuccessHandler = async () => {
                    await createTask?.(
                      question,
                      user.user_guid,
                      caseId,
                      backendTaskRef,
                      due_date_value.toISOString(),
                      appointmentId,
                      surveyjsId,
                      taskQueue === 'Client queue' ? '0' : '1'
                    );
                    disableActionButton(actionButton);
                  };
                  // customHBSOnComplete is not SurveyJS and is created & attached by us
                  // SurveyJS doesn't fully support async, this is our way of getting around this
                  survey.customHBSOnComplete = completionSuccessHandler;
                  survey.completeLastPage();
                }
                break;
            }

            if (shouldDisableActionButton) {
              disableActionButton(actionButton);
            }

            console.log('handleAction', question);
            return Promise.resolve(undefined);
          }
        }

        const survey = new HBSModel();
        const surveyDefn = JSON.parse(surveyJson);
        // addManualTestQuestions(surveyDefn);

        survey.fromJSON(surveyDefn);

        interface SurveyElementNode {
          isPage?: boolean;
          title?: string;
          parent?: SurveyElementNode;
          getType?: () => string;
        }

        // Intercept the title rendering to apply semantic HTML heading tags (h1, h2, etc.)
        survey.onGetTitleTagName.add((sender, options) => {
          const element = options.element as SurveyElementNode;
          let depth = 0;

          // Check if the Main Survey has a title (and we aren't currently rendering it)
          if (element !== sender) {
            if (sender.title && typeof sender.title === 'string' && sender.title.trim() !== '') {
              depth++;
            }
          }

          // Walk up the component tree (Panels and Pages)
          let current = element.parent;

          // Loop through all parents until we hit the top.
          // (We ensure current !== sender to prevent counting the main survey twice)
          while (current && current !== sender) {
            // Only increase the heading depth if this parent actually has a visible title
            if (current.title && typeof current.title === 'string' && current.title.trim() !== '') {
              depth++; // Found a titled parent, increase the heading depth
            }
            current = current.parent; // Move up to the next parent
          }

          // Calculate the intended heading level
          const finalLevel = 1 + depth;

          // Safely check if the element is a Page
          const isPageElement = element.getType?.() === 'page';

          // If a standard question or panel is about to become an H1 (because no parent titles exist),
          // strip the heading status completely and render it as a normal div.
          if (finalLevel === 1 && !isPageElement && element !== sender) {
            options.tagName = 'div';
          } else {
            // Otherwise, apply the calculated heading tag
            // Math.min ensures we never go past <h6>, which is the strict HTML limit.
            options.tagName = `h${Math.min(finalLevel, 6)}`;
          }
        });

        if (readonly) survey.mode = 'display';

        // @ts-ignore - Survey theme version compatibility
        survey.applyTheme(surveyTheme);

        survey.onLoadChoicesFromServer.add(function (
          survey,
          options: {
            serverResult: { cats: { cat_code: string; no_areas: number; needs_card_details: boolean }[] };
          }
        ) {
          if (Object.keys(options.serverResult).length > 0) {
            const currentMultiSpecialties = (survey?.data?.multiSpecialtes || []) as string[];
            const specialtiesWithMultipleAreas = options?.serverResult?.cats?.filter((cat) => cat?.no_areas > 0) || [];
            const newMultiSpecialties = specialtiesWithMultipleAreas?.map((cat) => cat?.cat_code);

            const currentSpecialtiesWithCardDetailsNeeded = survey?.data?.specialtiesWithCardDetailsNeeded || [];
            const specialtiesWithCardDetailsNeeded =
              options?.serverResult?.cats?.filter((cat) => cat?.needs_card_details) || [];
            const newCurrentSpecialtiesWithCardDetailsNeeded = specialtiesWithCardDetailsNeeded?.map(
              (cat) => cat?.cat_code
            );

            survey.data = {
              ...survey.data,
              multiSpecialtes: [...currentMultiSpecialties, ...newMultiSpecialties],
              specialtiesWithCardDetailsNeeded: [
                ...currentSpecialtiesWithCardDetailsNeeded,
                ...newCurrentSpecialtiesWithCardDetailsNeeded
              ]
            };
          }
        });
        survey.onTextMarkdown.add(function (_, options) {
          let new_text = options?.text;
          // Parse anchor tags
          if (options?.text?.includes('</a>')) {
            new_text = '<p>' + new_text + '</p>';
          }
          options.html = new_text;
        });
        survey.currentPageNo = 0;
        survey.showCompletedPage = false;
        survey.textUpdateMode = 'onTyping';

        if (answers) {
          const expandedAnswers = await getExpandedAnswers?.(answers, surveyjsId);
          Object.assign(answers, expandedAnswers);
          survey.data = JSON.parse(JSON.stringify(answers));
          if (pageType === 'patients' && incomplete) {
            survey.currentPageNo = answers.currentPageNo;
          }
        }

        survey.onValueChanged.add((sender, { name }) => {
          if (name === 'Consoutcome2') {
            sender.setValue('Consoutcome2Formatted', CustomizePatientAvailability(sender.getValue('Consoutcome2')));
          }
        });

        // The General Health survey can be edited after it is completed
        let surveyIsEditable: boolean = incomplete || cat_code === 'GH';
        if (!caseReview && (always_edit || incomplete)) {
          if ((pageType === 'clinician' && clin_editable) || cat_code === 'INVREQ') {
            surveyIsEditable = true;
          }
          if (pageType === 'patients') {
            surveyIsEditable = true;
          }
        }

        /* For any file question type that has no 'acceptedTypes' property, default
         * to DEFAULT_ACCEPTED_TYPES. */
        survey.onAfterRenderQuestion.add((_sender, options) => {
          if (options.question instanceof QuestionFileModel && !options.question['acceptedTypes']) {
            (options.question['acceptedTypes'] as any) = DEFAULT_ACCEPTED_TYPES.join(',');
          }
        });

        survey.onAfterRenderQuestion.add((_sender, options) => {
          if (options.question.getType() !== 'imagepicker') return;

          // Prevent announcing images by screen readers
          options.htmlElement.querySelectorAll('.sd-imagepicker__image').forEach((img) => {
            img.setAttribute('alt', '');
            img.setAttribute('aria-hidden', 'true');
          });

          // Prevent announcing imagepicker option text by screen readers
          options.htmlElement.querySelectorAll('.sd-imagepicker__text').forEach((text) => {
            text.setAttribute('aria-hidden', 'true');
          });
        });

        /* The file question type doesn’t honour the acceptedTypes property when the
         * file is dragged and dropped rather than selected from the file dialog.
         * ...so, this clunky workaround is necessary. */
        survey.onValueChanged.add((sender, { question, value }) => {
          if (!(question instanceof QuestionFileModel)) return;

          const acceptedTypes = getAcceptedTypesFromQuestion(question);
          const acceptedTypesDisplayNames = formatAcceptedTypesDisplayNames(acceptedTypes);
          const files: File[] = value || [];

          if (files.some((file) => !acceptedTypes.includes(file.type))) {
            files.forEach((file) => {
              if (!acceptedTypes.includes(file.type)) {
                sender.setValue(
                  // @ts-ignore - Survey question type compatibility
                  question.name,
                  files.filter(({ name }) => name !== file.name)
                );
              }
            });

            errorModal({
              title: 'Unsupported file type',
              content: `The following file types are supported: ${acceptedTypesDisplayNames}`
            });
          }
        });

        const onCurrentPageChangedHandler = () => {
          // To avoid missing context of page, scroll to top of survey instead of focusing on first question
          // 83px is the height of the header (with padding)
          window?.scrollTo(0, 83);
        };
        survey.focusFirstQuestionAutomatic = false;
        survey.onCurrentPageChanged.add(onCurrentPageChangedHandler);

        if (surveyIsEditable && !isSavingAsComplete) {
          const onValueChangedHandler = async (sender: any, options: any) => {
            if (isCompleting.current) return;
            if (options?.question?.actionType === 'Book appointment') return;

            if (!shouldComplete) {
              setUpdatedSurveyRes((previous) => ({
                ...previous,
                response: JSON.stringify(sender.data)
              }));

              return;
            }

            const owner_guid = surveyRes.patient_user_guid || user_guid;
            await uploadEmbeddedImagesToS3?.(sender, owner_guid, surveyjsId, dispatch, appointmentId);

            const surv_data = sender.data;

            surv_data.currentPageNo = survey.currentPageNo;

            setIsSaving(true);
            const surveyDetails = {
              user_guid,
              surveyjs_id: surveyjsId,
              complete: false,
              response_json: JSON.stringify(await removeEmbeddedImages?.(surv_data), (key, value) =>
                key === 'dataURL' || key === 'highRes' ? undefined : value
              )
            };
            await saveSurvey?.(surveyDetails).then(() => setIsSaving(false));

            setUpdatedSurveyRes({
              ...updatedSurveyRes,
              response: surveyDetails.response_json
            });
            // @ts-ignore - Survey model type compatibility
            onData?.(survey, surveyjsId);
          };
          survey.onValueChanged.add(debounce(onValueChangedHandler, 1000));

          /**
           * Our onCompletingHandler is attached to the onCompleting event
           * This event runs before the survey completes (onComplete runs after the survey completes)
           * The outcome of the onCompleting event decides if the onComplete event is triggered
           * And the outcome is dictated by setting options.allowComplete
           * Although onCompletingHandler is defined as async, surveyJS's onComplete event doesn't wait for the promise to resolve
           * We have to set allowComplete to false initially to prevent surveyJS skipping past and completing the survey before we do all our stuff
           * We set allowComplete to true at the end of the function - we believe this then triggers the onComplete event
           */
          const onCompletingHandler = async (sender: any, options: any) => {
            options.allowComplete = false;

            if (survey.redFlagOutcome && outcome.current.length === 0) {
              outcome.current = 'RED-FLAG';
            } else if (outcome.current !== 'RED-FLAG' && isDermoscopyOutcome(sender.data, surveyDefn)) {
              outcome.current = 'DERMOSCOPY';
            }

            if (!survey.skipConfirmation) {
              const continueSubmission = await new Promise<boolean>((resolve) => {
                confirmationModal({
                  title: confirmationModalTitle,
                  zIndex: 100000004,
                  mask: true,
                  maskClosable: false,
                  onOk: () => resolve(true),
                  onCancel: () => {
                    resolve(false);
                  }
                });
              });

              if (!continueSubmission) return;
            }

            if (surveyRes.outcome_mandatory_flag && outcome.current.length === 0) {
              infoModal({
                title: 'Cannot complete survey',
                content: 'An outcome must be specified for this survey'
              });
              return;
            }
            isCompleting.current = true;
            setModalVisible?.(true);

            const caseAllowsOutcome = surveyRes.outcome_flag;
            if (caseAllowsOutcome && outcome.current.length > 0 && caseId) {
              await saveTriage(
                outcome.current,
                user_guid,
                caseId,
                savedAreaCode.current,
                savedClinicianTypeCode.current,
                dischargeCode.current
              );
              setProgressMessage('Saving triage...');
              setCompletingPercent(10);
            }

            if (survey.customHBSOnComplete) {
              await survey.customHBSOnComplete();
            }

            const surveyData = sender.data;
            surveyData.currentPageNo = survey.currentPageNo;

            const saveSurveyAsCompleteAsync = async () => {
              if (isSaving) {
                return;
              } else {
                setIsSavingAsComplete(true);

                try {
                  await saveSurveyToS3?.(
                    surveyData,
                    survey.toJSON({ storeDefaults: true }),
                    {
                      survey_id: surveyjsId,
                      patient_user_guid: surveyRes.patient_user_guid || user_guid,
                      cat_code
                    },
                    'survey.pdf',
                    dispatch,
                    caseId,
                    SURVEYJS_LICENSE_KEY,
                    setCompletingPercent,
                    setProgressMessage,
                    (val) => setModalVisible?.(val)
                  );

                  const completedSurveyDataWithoutImages = await removeEmbeddedImages?.(surveyData);
                  setProgressMessage('Saving survey...');
                  setCompletingPercent(95);

                  /* This request normally sends the 'final' survey response to the
                  backend and sets the survey as complete. ...however, if the triage
                  is saved as a DISCHARGE, the backend auto-completes the survey,
                  after which we can no longer save new survey responses. We have to
                  skip this step if the triage is a DISCHARGE. */

                  /* We also need to skip this step if the triage is a DERMOSCOPY as
                  the outcome needs to be provided directly to the request via saveTriage
                  for the backend to receive it correctly */

                  const skippedOutcomes = ['DISCHARGE', 'DERMOSCOPY'];
                  if (!skippedOutcomes.includes(outcome.current)) {
                    await saveSurvey?.({
                      user_guid,
                      surveyjs_id: surveyjsId,
                      complete: true,
                      response_json: JSON.stringify(completedSurveyDataWithoutImages)
                    });
                  }
                  setProgressMessage('Survey saved');
                  surveyAnswersOnComplete?.(surveyData);
                  setCompletingPercent(100);
                  survey.showCompletedPage = !setModalVisible;
                } catch {
                  setIsSavingAsComplete(false);
                  isCompleting.current = false;
                }
              }
            };
            await callWithRetry(saveSurveyAsCompleteAsync);

            options.allowComplete = true;
          };
          survey.onCompleting.add(onCompletingHandler);
        } else if (!always_edit) {
          survey.mode = 'display';
        }

        const onUploadFilesHandler = async (_sender: Model, options: UploadFilesEvent) => {
          if (!uploadFileToS3) return;

          const { files, question } = options;

          const acceptedTypes = getAcceptedTypesFromQuestion(question);
          const acceptedTypesDisplayNames = formatAcceptedTypesDisplayNames(acceptedTypes);

          if (files.some((file) => !acceptedTypes.includes(file.type))) {
            files.forEach((file) => {
              if (!acceptedTypes.includes(file.type)) {
                const filteredFiles = files.filter(({ name }) => name !== file.name);

                return options.callback(filteredFiles, [
                  `'${file.name}' is not a supported file type. Supported file types are: ${acceptedTypesDisplayNames}.`
                ]);
              }
            });

            errorModal({
              title: 'Unsupported file type',
              content: `The following file types are supported: ${acceptedTypesDisplayNames}.`
            });

            return;
          }

          // see https://surveyjs.io/form-library/examples/questiontype-file/reactjs#content-code
          const images = await Promise.all(
            options.files.map((file) =>
              uploadFileToS3(file, dispatch, surveyRes.patient_user_guid, userHasReadPermissions, surveyjsId)
                .then((result) => {
                  return {
                    file: result.file || file,
                    content: result.url,
                    failed: false
                  };
                })
                .catch((e) => {
                  console.error('UPLOAD FAILED', e, file);
                  return { file, content: undefined, failed: true };
                })
            )
          );

          if (!userHasReadPermissions) {
            images.forEach((i) => (i.content = previewUnavailable));
          }

          if (images.find((i) => i.failed)) {
            const file = images.find((i) => i.failed)?.file;

            Modal.error({
              content: `"${file?.name}" failed to upload. Please try again.`
            });
            return;
          }

          try {
            options.callback('success', images);
          } catch (e) {
            Modal.error({
              content: 'File upload failed. Please try again.'
            });
            console.warn('Files failed to upload', e);
          }
        };

        // @ts-ignore - Survey handler type compatibility
        survey.onUploadFiles.add(onUploadFilesHandler);

        survey.onRegionOfInterestImageUpload = async (images: unknown[]) => {
          // Upload images to S3
          const owner_guid = surveyRes.patient_user_guid || user_guid;
          const res = await uploadImagesWithCropToS3?.(
            images,
            owner_guid,
            surveyjsId,
            dispatch,
            userHasReadPermissions
          );
          // return URL
          return res;
        };

        survey.onRegionOfInterestImageDownload = async (image: { name: string }) => {
          return getSurveyImageFromS3?.(
            image.name,
            surveyRes.patient_user_guid || user_guid,
            userHasReadPermissions,
            '',
            dispatch
          );
        };

        survey.showCookieSettings = () => {
          showCookieSettings();
        };

        const apiURL = new URL(API_URL);

        if (incomplete) {
          const users = await getUsers();
          if (users?.[0]) {
            survey.data = {
              ...survey.data,
              CanSelfRefer: users[0].self_refer,
              PatientFirstName: users[0].first_name,
              PatientLastName: users[0].last_name,
              PatientHasNoSpecifiedSex: !['Male', 'Female'].includes(users[0].sex),
              Urgency: await getCaseUrgency?.(),
              RestrictedUser: userIsRestricted,
              API_HOST: `${apiURL.host}${apiURL.pathname}`,
              APPUSER: user_guid,
              MembershipNo: users[0].membership_no,
              UserNeedsRecentCardDetails: false /*userNeedsRecentCardDetails*/,
              SourceURL: users[0].source_url,
              Pathway: surveyRes.pathway,
              PatientDefaultAddress: surveyLabTestOrder && combineOrderAddressParts(surveyLabTestOrder.patient_address),
              RandoxLabTests: surveyLabTestOrder && combineOrderLabTests(surveyLabTestOrder.lab_tests),
              RandoxOrderHasUrineTest: surveyLabTestOrder && hasOrderUrineTest(surveyLabTestOrder.lab_tests)
            };
          }
        }

        setSurveyModel(survey);
        setIsConfiguringSurveyModel(false);
      };
      configureSurveyModel().then();
    }

    return () => clearInterval(credentialsPoll);
  }, [
    rawSurveyDefinition,
    caseReview,
    dispatch,
    getExpandedAnswers,
    getUsers,
    onData,
    outcome,
    pageType,
    removeEmbeddedImages,
    saveSurvey,
    setModalVisible,
    surveyRes,
    surveyjsId,
    uploadEmbeddedImagesToS3,
    uploadFileToS3,
    user,
    user_guid,
    surveyLabTestOrder
    // todo - one or more of these dependencies are not stable and cause the UI to
    //        flicker badly when making changes
    // updatedSurveyRes,
    // isCompleted,
    // isSavingAsComplete,
    // shouldComplete,
    // caseId,
    // saveTriage,
    // isSaving,
    // saveSurveyToS3,
    // userHasReadPermissions,
    // uploadImagesWithCropToS3,
    // getSurveyImageFromS3,
    // getCaseUrgency,
    // userIsRestricted,
    // API_URL,
    // userNeedsRecentCardDetails
  ]);

  useEffect(() => {
    surveyLocalization.locales.en.completingSurvey = 'Thank you for submitting your answers.';
  }, []);

  const onModalSubmission = async () => {
    routeBackToDashboard?.();
  };

  const downloadFileFromS3: (file: FileWithoutContent) => Promise<FileWithContent> = useCallback(
    async (file: { type: string; name: string; url: string; crop?: any }) => {
      if (!file) return file;

      if (!getSurveyImageFromS3)
        return Promise.resolve({
          content: '',
          name: file.name,
          url: file.url,
          type: file.type,
          crop: file.crop
        });

      const res = await getSurveyImageFromS3(
        file.name,
        surveyRes?.patient_user_guid || user_guid,
        !userIsRestricted,
        surveyjsId,
        dispatch,
        false
      );
      return {
        ...file,
        content: res
      };
    },
    [dispatch, getSurveyImageFromS3, surveyjsId, userIsRestricted, user_guid]
  );

  return (
    <>
      {isLoadingSurveyData || isConfiguringSurveyModel ? (
        <div style={{ textAlign: 'center' }} data-testid="loading-spinner-component">
          <Spin size="large" tip={isLoadingSurveyData ? 'Loading...' : 'Configuring...'} />
        </div>
      ) : (
        <VLSurveyStyleOverrides>
          {inTableView ? (
            <SurveyTableView
              surveyModel={surveyModel}
              surveyRes={surveyRes}
              surveyDef={rawSurveyDefinition}
              downloadFileFromS3={downloadFileFromS3}
            />
          ) : (
            <>
              {surveyModel && <Survey surveyModel={surveyModel} />}
              {surveyModel && (
                <SurveySubmissionModal
                  title="Submitting Survey"
                  modalVisible={modalVisible}
                  onModalSubmission={onModalSubmission}
                  setModalVisible={setModalVisible}
                  surveyModel={surveyModel}
                  surveyRes={surveyRes}
                  neverShowEditableMessage={userIsRestricted}
                  completingPercent={completingPercent}
                  progressMessage={progressMessage}
                />
              )}
            </>
          )}
        </VLSurveyStyleOverrides>
      )}
    </>
  );
};

export default CustomSurvey;
export { CustomSurvey };

function getAcceptedTypesFromQuestion(question: QuestionFileModel | QuestionSignaturePadModel): string[] {
  if (!question.acceptedTypes.trim()) return DEFAULT_ACCEPTED_TYPES;

  return question.acceptedTypes.split(',');
}

function formatAcceptedTypesDisplayNames(acceptedTypes: string[]): string {
  return acceptedTypes.map((type) => type.split('/')[1].toUpperCase()).join(', ');
}
